import { AuxSelectorDataType } from '../../types';
import {
  AuxSelectorFilter,
  BillsAndPaymentsSearchType,
  BillType,
  SelectionProjectStageFilterSendType,
  VariableType,
} from '@/models/etc/types';
import type { SelectorStrategy, StrategyContext } from './types';
import { resolveFromFilter, resolveToFilter } from './types';
import { makeBillLoader } from './bill-loader';
import { matchesText } from '../utils/text-filter';
import { hintsRequestStarted, hintsRequestDone } from '../utils/hints-diag';

import { analyticsCommentsStore } from '@/store/analytics-comments';
import { auxStore } from '@/store/auxillary';
import { financesStore } from '@/store/finances';
import { generalStore } from '@/store/general';
import { storageMovementsStore } from '@/store/storage/storage-movements';
import { storagePositionsStore } from '@/store/storage/storage-positions';
import { SingleIdRequest } from '@/models/interfaces/http/common.http.request';
import { storageStore } from '@/store/storage/storage-instances';

import type { AnalyticsCommentTemplateDtoList } from '@/models/interfaces/analytics/analytics-comments';
import type { CommonEntity, CommonListGiver } from '@/models/interfaces/common';
import type { AddressAutocompleteChain, AddressInputContainer, AddressInputComponentOut } from '@/models/interfaces/entity-settings';
import type { SelectionFilterWorkersContainer } from '@/models/interfaces/workers/worker-models';
import type {
  CommonEntityWithCount,
  DefaultParameterHintsResponse,
  ParameterContainerFilledHintsRequest,
  StorageItemNameFilterDto,
  StorageItemSearched,
  StorageItemsSearchRequest,
  StorageDetailsTemplateDtoList,
} from '@/models/interfaces/storage/storage-models';

// ---------------------------------------------------------------------------
// Shared converter pairs
// ---------------------------------------------------------------------------

const entityWrapper = {
  fromFilter: (ent: any) => ent.Entity,
  toFilter: (ent: any, orig?: any) => ({
    Entity: ent,
    Roles: orig?.Roles ?? [],
    Positive: orig?.Positive ?? true,
  }),
};

const storageMovementWrapper = {
  fromFilter: (ent: any) => ent.Storage,
  toFilter: (ent: any, orig?: any) => ({
    Storage: ent,
    TypeFlags: orig?.TypeFlags ?? 3,
  }),
};

const projectStageWrapper = {
  fromFilter: (ent: any) => ent.Stage,
  toFilter: (ent: any, orig?: any) => {
    const defDate = new Date();
    return {
      Positive: orig?.Positive ?? true,
      StartDate: orig?.StartDate ?? defDate,
      EndDate: orig?.EndDate ?? undefined,
      EndDateIsToday: orig?.EndDateIsToday ?? true,
      Stage: ent,
      FilterType: orig?.FilterType ?? SelectionProjectStageFilterSendType.NowOnStage,
    };
  },
};

// ---------------------------------------------------------------------------
// Workers strategy with closure-based cache
// ---------------------------------------------------------------------------

function createWorkersStrategy(chartVariant: boolean): SelectorStrategy {
  let usersCache: any[] | null = null;

  function filterDeps(deps: any[], filter: string) {
    const normalizedFilter = (filter || '').trim();
    return deps.map((dep: any) => ({
      department: dep.department,
      // Если совпал заголовок департамента, показываем всех детей как в tree-selector семантике.
      child: matchesText(dep.department || '', normalizedFilter)
        ? dep.child
        : dep.child.filter((el: any) => matchesText(el.Name, normalizedFilter)),
    }));
  }

  return {
    fromFilter: chartVariant ? undefined : (ent: any) => ent.Entity,
    toFilter: entityWrapper.toFilter,
    async loadMore(filter, count, offset, ctx) {
      if (offset > 0) return [];

      const store = auxStore();
      if (usersCache) return filterDeps(usersCache, filter as string);

      const tmpw = (await store.getFilterWorkers()).data as SelectionFilterWorkersContainer;
      usersCache = [
        { removed: false, department: ctx.t('no_department'), child: [] as any[] },
        {
          removed: true,
          department: ctx.t('removed_users'),
          child: tmpw.RemovedWorkers.filter((el: any) => matchesText(el.Name, filter as string)),
        },
      ];

      for (const el of tmpw.ActualWorkers) {
        if (el.IsWorker && matchesText(el.Worker.Name, filter as string)) {
          usersCache[0].child.push({ department: '', ...el.Worker });
        } else if (el.Departament?.Workers) {
          usersCache.push({
            department: el.Departament.Name,
            child: el.Departament.Workers.filter((w: any) => w?.Id).map((work: any) => ({
              department: el.Departament.Name,
              ...work,
            })),
          });
        }
      }

      return filterDeps(usersCache, filter as string);
    },
    resetCache() { usersCache = null; },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: true,
    isObjectFilter: false,
    singleLoad: true,
    label: 'selector_system_workers',
  };
}

// ---------------------------------------------------------------------------
// ProjectStages strategy with closure-based cache
// ---------------------------------------------------------------------------

function createProjectStagesStrategy(): SelectorStrategy {
  let stagesCache: any[] | null = null;

  return {
    ...projectStageWrapper,
    async loadMore(filter, count, offset): Promise<any[]> {
      if (offset > 0) return [];

      if (!stagesCache) {
        const store = auxStore();
        const data = (await store.getFilterStages()).data;
        stagesCache = data ?? [];
      }
      const cache = stagesCache ?? [];
      return cache.filter((el: any) => matchesText(el.Name, filter as string));
    },
    resetCache() { stagesCache = null; },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    label: 'selector_system_stages',
  };
}

// ---------------------------------------------------------------------------
// Counterparties strategy
// ---------------------------------------------------------------------------

function createCounterpartiesStrategy(): SelectorStrategy {
  let cache: any[] | null = null;
  let cachedFilter: string | null = null;

  return {
    // Костыль: эндпоинт не принимает пагинацию и отдаёт весь отфильтрованный список одним куском,
    // поэтому порции для скролла нарезаем на клиенте. Когда сервер начнёт принимать Count и Offset:
    // убрать cache, cachedFilter и resetCache, передать их в тело запроса и вернуть List как есть.
    async loadMore(filter, count, offset) {
      const filterStr = (filter as string) ?? '';
      if (cache === null || cachedFilter !== filterStr) {
        const store = financesStore();
        const result = (await store.counterpartyGetFilteredList({
          Filter: filterStr,
          ForBills: true,
        })) as CommonListGiver;
        cache = result?.List ?? [];
        cachedFilter = filterStr;
      }
      return cache.slice(offset, offset + count);
    },
    resetCache() { cache = null; cachedFilter = null; },
    elementType: VariableType.Counterparty,
    closeAfterSelect: false,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// ClientsToReattach strategy
// ---------------------------------------------------------------------------

function createClientsToReattachStrategy(): SelectorStrategy {
  let cache: any[] | null = null;
  let cachedFilter: string | null = null;

  return {
    // Костыль: сервер пока игнорирует count и offset и отдаёт всех клиентов сразу, порции режем сами.
    // Когда пагинация появится: убрать cache, cachedFilter и resetCache, а count и offset передать
    // в getReattachClients, она их уже умеет отправлять в query.
    async loadMore(filter, count, offset) {
      const filterStr = (filter as string) ?? '';
      if (cache === null || cachedFilter !== filterStr) {
        const store = financesStore();
        cache = await store.getReattachClients(filterStr);
        cachedFilter = filterStr;
      }
      return cache.slice(offset, offset + count);
    },
    resetCache() { cache = null; cachedFilter = null; },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_clients',
  };
}

// ---------------------------------------------------------------------------
// Storages strategy (loads from store property, filters locally)
// ---------------------------------------------------------------------------

function createStoragesStrategy(labelKey: string): SelectorStrategy {
  return {
    async loadMore(filter, count, offset) {
      if (offset > 0) return [];
      const store = storageStore();
      await store.ensureStoragesWithItems();
      const all = store.storagesWithItems as CommonEntity[];
      return all.filter((el: CommonEntity) => matchesText(el.Name, filter as string));
    },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    label: labelKey,
  };
}

// ---------------------------------------------------------------------------
// Storage items strategy (object filter)
// ---------------------------------------------------------------------------

function createStorageItemsStrategy(): SelectorStrategy {
  return {
    async loadMore(filter, count, offset, ctx) {
      const f = filter as AuxSelectorFilter;
      const poStore = storagePositionsStore();
      if (!f.objFilter || (!f.objFilter.component?.container && !f.objFilter.component?.vendorCode)) {
        if (offset === 0) {
          // align with legacy selector: notify parent that no data is available
          ctx.emit('dataAvailable', false);
        }
        return [];
      }
      if (f.objFilter.component?.container?.TemplateId === 998) return [];
      const items = await poStore.searchStorageItems({
        NameFilter: {
          VendorCode: f.objFilter.component?.vendorCode,
          NameParametersContainer: f.objFilter.component?.container,
        } as StorageItemNameFilterDto,
        StorageFilter: f.objFilter.component?.storageForFilter > 0
          ? [f.objFilter.component.storageForFilter]
          : null,
        Count: count,
        Offset: offset,
      } as StorageItemsSearchRequest);
      if (offset === 0) {
        ctx.emit('dataAvailable', Array.isArray(items) && items.length > 0);
      }
      return items ?? [];
    },
    elementType: VariableType.ParameterInline,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: true,
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// Address hints strategy
// ---------------------------------------------------------------------------

function createAddressHintsStrategy(): SelectorStrategy {
  return {
    async loadMore(filter, count, offset, ctx) {
      if (offset > 0) return [];

      const levels = (ctx.componentModel?.container ?? []) as AddressInputComponentOut[];
      if (!Array.isArray(levels) || !levels.length) {
        return [];
      }

      const activeLevel = Number(ctx.componentModel?.activeLevel ?? 0);
      const request: AddressInputContainer = {
        Components: levels.map((el) => ({
          AddressNodeId: el.AddressNodeId,
          Level: Number(el.Level),
          Value: Number(el.Level) > activeLevel
            ? ''
            : (el.Value || ''),
        })),
        ActiveLevel: activeLevel,
      };

      const store = auxStore();
      const chains = (await store.getAddressHints(request)).data as AddressAutocompleteChain[] | undefined;
      return (chains ?? []).map((el) => ({
        Id: el.SheetId,
        Name: el.Components.map((c) => c.Value).join(', '),
        DisplayName: el.Components.map((c) => `${c.Value} (${c.Count})`).join(', '),
        ComponentsValues: el.Components.map((c) => c.Value),
        Components: el.Components,
      }));
    },
    elementType: VariableType.AddressInline,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// DefaultParameterHints strategy (complex branching logic)
// ---------------------------------------------------------------------------

// Загрузка подсказок набора с записью в [hints][diag]
function withHintsDiag(
  type: AuxSelectorDataType,
  describe: (filter: string | AuxSelectorFilter, ctx: StrategyContext) => { id: number | undefined; filter: string; container: string },
  load: SelectorStrategy['loadMore'],
): SelectorStrategy['loadMore'] {
  return async (filter, count, offset, ctx) => {
    const d = describe(filter, ctx);
    const request = hintsRequestStarted(type, d.id, d.filter, d.container, offset);
    const res = await load(filter, count, offset, ctx);
    hintsRequestDone(request, res?.length ?? 0);
    return res;
  };
}

function createDefaultParameterHintsStrategy(): SelectorStrategy {
  return {
    loadMore: withHintsDiag(
      AuxSelectorDataType.DefaultParameterHints,
      (filter) => {
        const f = filter as AuxSelectorFilter;
        const container = f?.objFilter?.component?.container;
        return {
          id: f?.objFilter?.setId,
          filter: f?.stringFilter ?? '',
          container: container ? JSON.stringify(container) : '',
        };
      },
      async (filter, count, offset, ctx) => {
      const f = filter as AuxSelectorFilter;
      if (!f.objFilter?.component?.container) return [];
      if (f.objFilter.component.container.TemplateId === 998) return [];

      const poStore = storagePositionsStore();

      if (f.objFilter.setId === 998) {
        if (!f.objFilter.component?.container && !f.objFilter.component?.vendorCode) return [];

        if (f.objFilter.component?.vendorCode || !f.objFilter.component?.isEmpty) {
          const sitems = await poStore.searchStorageItems({
            NameFilter: {
              VendorCode: f.objFilter.component?.vendorCode,
              NameParametersContainer: f.objFilter.component?.container,
            } as StorageItemNameFilterDto,
            StorageFilter: f.objFilter.component?.storageForFilter > 0
              ? [f.objFilter.component.storageForFilter]
              : null,
            Count: 100000,
            Offset: offset > 0 ? 100000 : 0,
          } as StorageItemsSearchRequest);

          if (!sitems) return [];
          const seen = new Map<number, { Id: number; Name: string }>();
          for (const item of sitems as StorageItemSearched[]) {
            for (const q of item.QuantitiesInStock || []) {
              if (!seen.has(q.Id)) seen.set(q.Id, { Id: q.Id, Name: q.Name });
            }
          }
          return Array.from(seen.values());
        } else {
          const sStore = storageStore();
          await sStore.ensureStoragesWithItems();
          const all = sStore.storagesWithItems as CommonEntity[];
          return all.filter((el: CommonEntity) => matchesText(el.Name, f.stringFilter));
        }
      }

      const defhints = (await poStore.instanceGetHintsToInstanceContainerFilledDefault({
        Container: f.objFilter.component?.container,
        HintedTemplateId: f.objFilter.setId,
        Count: count,
        Offset: offset,
        Filter: f.stringFilter,
      } as ParameterContainerFilledHintsRequest)) as DefaultParameterHintsResponse;

      ctx.emit('hintedSetType', defhints?.HintedSetType ?? null);
      return defhints.Hints;
    }),
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: true,
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// StringSets strategy
// ---------------------------------------------------------------------------

function createStringSetsStrategy(): SelectorStrategy {
  return {
    async loadMore(filter, count, offset, ctx) {
      const store = auxStore();
      const emptyEntry = ctx.allowFreeInput && !ctx.searchCache.length
        ? [{ Id: -10, Name: ctx.t('empty_value') }]
        : [];
      const apiData = (await store.getFilterStringSets(filter as string, ctx.setId, count, offset)).data;
      const res = [...emptyEntry, ...apiData];
      ctx.searchCache.push(...res);
      ctx.emit('chunkLoaded', undefined);
      return res;
    },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    // ВАЖНО: НЕ задаём allowMultipleOverride. Раньше тут стоял `false`, что насильно
    // переключало даже общий «+» меню добавления значений в single-select. Это ломало
    // multi-select сценарий (новое значение из списка ЗАМЕНЯЛО первое существующее
    // вместо добавления). Теперь allowMultiple берётся из props компонента-потребителя.
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// InstanceValueSet strategy (набор значений вхождения / поля вне склада)
// ---------------------------------------------------------------------------

// Шаблоны, которые ещё можно прикрепить к сущности. Сервер отдаёт список целиком,
// поэтому страницы после первой пустые, а поиск режется здесь.
// setId это id сущности, preferredSegment это тип прикрепления.
// Пустой фильтр берёт список с сервера заново.
function createParameterTemplatesStrategy(): SelectorStrategy {
  let cache: CommonEntity[] | null = null;
  let cacheKey = "";

  return {
    async loadMore(filter, _count, offset, ctx) {
      if (offset > 0) return [];
      const key = `${ctx.setId}:${ctx.preferredSegment}`;
      const f = ((filter as string) ?? "").trim();
      if (!cache || cacheKey !== key || !f) {
        const { parameterStore } = await import("@/store/parameters/parameter-store");
        const data = (await parameterStore().getTemplatesToAttach(
          ctx.setId,
          ctx.preferredSegment,
        )) as CommonEntity[] | undefined;
        cache = data ?? [];
        cacheKey = key;
      }
      return f ? cache.filter((el) => matchesText(el.Name ?? "", f)) : [...cache];
    },
    resetCache() {
      cache = null;
      cacheKey = "";
    },
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    allowMultipleOverride: false,
    label: "parameters_label",
  };
}

function createInstanceValueSetStrategy(): SelectorStrategy {
  return {
    loadMore: withHintsDiag(
      AuxSelectorDataType.InstanceValueSet,
      (filter, ctx) => ({
        id: ctx.instanceId,
        filter: typeof filter === 'string' ? filter : '',
        container: '',
      }),
      async (filter, count, offset, ctx) => {
      if (!ctx.instanceId) return [];
      const poStore = storagePositionsStore();
      const text = typeof filter === 'string' ? filter : '';
      const asker = { Id: ctx.instanceId } as SingleIdRequest;
      const defhints = ctx.isParameterInstance === false
        ? await poStore.parameterInstanceGetHintsToField(asker, count, offset, text)
        : await poStore.parameterInstanceGetHintsToParameter(asker, count, offset, text);
      if (offset === 0) {
        ctx.emit('hintedSetType', defhints?.HintedSetType ?? null);
      }
      return defhints?.Hints ?? [];
    }),
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  };
}

// ---------------------------------------------------------------------------
// Strategy map
// ---------------------------------------------------------------------------

export const strategies: Record<AuxSelectorDataType, SelectorStrategy> = {
  [AuxSelectorDataType.Tags]: {
    ...entityWrapper,
    loadMore: async () => [],
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    label: 'selector_system_tags',
  },

  [AuxSelectorDataType.Clients]: {
    ...entityWrapper,
    async loadMore(filter, count, offset) {
      return (await auxStore().getFilterClients(filter as string, count, offset)).data;
    },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_clients',
  },

  [AuxSelectorDataType.Projects]: {
    ...entityWrapper,
    async loadMore(filter, count, offset) {
      return (await auxStore().getFilterProjects(filter as string, count, offset)).data;
    },
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_projects',
  },

  [AuxSelectorDataType.ProjectStage]: createProjectStagesStrategy(),

  [AuxSelectorDataType.Workers]: createWorkersStrategy(false),
  [AuxSelectorDataType.LinearChartWorkers]: createWorkersStrategy(true),

  [AuxSelectorDataType.Parameters]: {
    loadMore: async () => [],
    elementType: VariableType.Entity,
    closeAfterSelect: false,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  },

  [AuxSelectorDataType.StringSets]: createStringSetsStrategy(),

  [AuxSelectorDataType.Lists]: {
    ...entityWrapper,
    async loadMore(filter, count, offset, ctx) {
      const result = (await auxStore().getFilterLists(
        filter as string, count, offset, ctx.excludeId, ctx.preferredSegment,
      )).data.Lists;
      return result;
    },
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: false,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_lists',
  },

  [AuxSelectorDataType.BillsForMovement]: {
    ...makeBillLoaderStrategy(
      BillsAndPaymentsSearchType.NewEntity,
      { requireType: BillType.Incoming },
    ),
  },

  [AuxSelectorDataType.BillsForExistingMovement]: {
    ...makeBillLoaderStrategy(
      BillsAndPaymentsSearchType.Movement,
      { requireType: BillType.Incoming, useSetId: true },
    ),
  },

  [AuxSelectorDataType.BillsForProject]: {
    ...makeBillLoaderStrategy(
      BillsAndPaymentsSearchType.Project,
      { useSetId: true },
    ),
  },

  [AuxSelectorDataType.ProjectsForMovement]: {
    async loadMore(filter, count, offset) {
      const store = storageMovementsStore();
      const result = await store.getMovementProjects(filter as string, count, offset);
      return result?.List ?? [];
    },
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_projects',
  },

  [AuxSelectorDataType.StorageItems]: createStorageItemsStrategy(),
  [AuxSelectorDataType.DefaultParameterHints]: createDefaultParameterHintsStrategy(),

  [AuxSelectorDataType.Storages]: createStoragesStrategy('selector_system_storages'),
  [AuxSelectorDataType.MovementStorages]: {
    ...storageMovementWrapper,
    ...createStoragesStrategy('selector_system_storages'),
  },

  [AuxSelectorDataType.StorageItemCommentTemplates]: {
    async loadMore(filter, count, offset) {
      const store = storagePositionsStore();
      const tmplts = await store.getNoteTemplates(filter as string, count, offset) as StorageDetailsTemplateDtoList;
      return tmplts.Templates;
    },
    async deleteItem(item) {
      const res = await storagePositionsStore().deleteNoteTemplate(item.Id);
      return !!res?.response_ok;
    },
    // Шаблоны инфо склада удаляет тот, у кого есть полный доступ к складу и правка приходов
    canDeleteItems: () => {
      const access = storageStore().storageAccess;
      return !!access?.CanWrite && !!access?.CanEditExistingIncome;
    },
    createdSignal: () => storagePositionsStore().note_template_created,
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  },

  [AuxSelectorDataType.MovementCommentTemplates]: {
    async loadMore(filter, count, offset) {
      const store = storageMovementsStore();
      const tmplts = await store.getMovementNoteTemplates(filter as string, count, offset) as StorageDetailsTemplateDtoList;
      return tmplts.Templates;
    },
    async deleteItem(item) {
      const res = await storageMovementsStore().deleteMovementNoteTemplate(item.Id);
      return !!res?.response_ok;
    },
    // Шаблоны действий склада удаляет тот, у кого есть полный доступ к складу и правка приходов
    canDeleteItems: () => {
      const access = storageStore().storageAccess;
      return !!access?.CanWrite && !!access?.CanEditExistingIncome;
    },
    createdSignal: () => storageMovementsStore().movement_note_template_created,
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  },

  [AuxSelectorDataType.TableCommentTemplates]: {
    async loadMore(filter, count, offset, ctx) {
      const store = analyticsCommentsStore();
      const tmplts = await store.getCommentTemplates(
        filter as string, ctx.preferredSegment, count, offset,
      ) as AnalyticsCommentTemplateDtoList;
      return tmplts?.Templates ?? [];
    },
    async deleteItem(item) {
      const res = await analyticsCommentsStore().deleteCommentTemplate(item.Id);
      return !!res?.response_ok;
    },
    // Табличные комментарии и их шаблоны доступны на правах просмотра аналитики
    canDeleteItems: () => !!generalStore().accessRights?.GeneralAccess,
    createdSignal: () => analyticsCommentsStore().comment_template_created,
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  },

  [AuxSelectorDataType.ClientsToReattach]: createClientsToReattachStrategy(),

  [AuxSelectorDataType.Counterparties]: createCounterpartiesStrategy(),

  [AuxSelectorDataType.CustomParameterHints]: {
    loadMore: async () => [],
    elementType: VariableType.Entity,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    singleLoad: true,
    label: 'selector_system_items',
  },
  [AuxSelectorDataType.AddressHints]: createAddressHintsStrategy(),
  [AuxSelectorDataType.InstanceValueSet]: createInstanceValueSetStrategy(),
  [AuxSelectorDataType.ParameterTemplates]: createParameterTemplatesStrategy(),
};

// ---------------------------------------------------------------------------
// Helper to create a bill strategy object (combines makeBillLoader with flags)
// ---------------------------------------------------------------------------

function makeBillLoaderStrategy(
  searchFrom: BillsAndPaymentsSearchType,
  opts?: { requireType?: BillType; useSetId?: boolean },
): SelectorStrategy {
  const loader = makeBillLoader(searchFrom, {
    requireType: opts?.requireType,
    getSearchFromId: opts?.useSetId ? (ctx) => ctx.setId : undefined,
  });
  return {
    loadMore: (f, c, o, ctx) => loader(f as string, c, o, ctx),
    elementType: VariableType.Bill,
    closeAfterSelect: true,
    hideCheckboxes: true,
    hasDeps: false,
    isObjectFilter: false,
    label: 'selector_system_items',
  };
}

// Re-export for convenience
export { resolveFromFilter, resolveToFilter };
export type { SelectorStrategy, StrategyContext };
